/**
 * Catálogo canónico de permisos de Atreus.
 *
 * Modelo: **permisos × roles** con overrides por tenant.
 *
 *   1. Cada módulo/feature declara sus permisos acá. Los IDs son strings
 *      dotted (`orders.charge`, `inventory.adjust`).
 *   2. `DEFAULT_PERMISSION_MATRIX` define el valor de fábrica por rol.
 *   3. Cada tenant puede sobreescribir cualquier celda vía la tabla
 *      `tenant_role_permissions` (granted=true/false). Si no hay fila,
 *      se usa el default.
 *   4. `hasPermission(role, id, overrides)` es la función canónica de
 *      evaluación. El backend la usa via `assertPermission`, el renderer
 *      via `usePermissions`.
 *
 * Solo el rol `admin` del tenant puede editar la matriz (enforced por
 * `settings.editPermissions`). El `superadmin` no la toca desde la UI
 * del tenant — si edita algo, lo hace desde el admin backoffice.
 */
export const permissions = {
    // -------------------- Orders --------------------
    'orders.view': {
        id: 'orders.view',
        name: 'Ver pedidos',
        description: 'Acceder a la lista de pedidos y sus detalles.',
        module: 'orders',
    },
    'orders.viewAll': {
        id: 'orders.viewAll',
        name: 'Ver pedidos de otros usuarios',
        description: 'Sin este permiso, el usuario solo ve los pedidos que abrió él mismo. Con el permiso, ve los de todos.',
        module: 'orders',
    },
    'orders.create': {
        id: 'orders.create',
        name: 'Crear pedidos',
        description: 'Iniciar un nuevo pedido desde cero.',
        module: 'orders',
    },
    'orders.editDraft': {
        id: 'orders.editDraft',
        name: 'Editar pedido en borrador',
        description: 'Agregar/quitar items y cambiar cantidades mientras el pedido sigue en draft.',
        module: 'orders',
    },
    'orders.advance': {
        id: 'orders.advance',
        name: 'Avanzar estado del pedido',
        description: 'Mover el pedido por los estados intermedios (confirmado, preparando, listo, entregado). No incluye cobrar.',
        module: 'orders',
    },
    'orders.charge': {
        id: 'orders.charge',
        name: 'Cobrar pedido',
        description: 'Marcar como pagado. Requiere caja abierta del propio usuario.',
        module: 'orders',
    },
    'orders.cancel': {
        id: 'orders.cancel',
        name: 'Cancelar pedido',
        description: 'Terminar el pedido como cancelado.',
        module: 'orders',
    },
    'orders.applyDiscount': {
        id: 'orders.applyDiscount',
        name: 'Aplicar descuentos',
        description: 'Editar el campo "descuento" del pedido. Política delicada — útil tenerlo restringido.',
        module: 'orders',
    },
    // -------------------- Products --------------------
    'products.view': {
        id: 'products.view',
        name: 'Ver catálogo',
        description: 'Acceder a la lista de productos (sin editar).',
        module: 'products',
    },
    'products.create': {
        id: 'products.create',
        name: 'Crear productos',
        description: 'Agregar nuevos productos al catálogo.',
        module: 'products',
    },
    'products.edit': {
        id: 'products.edit',
        name: 'Editar productos',
        description: 'Modificar campos del catálogo (nombre, SKU, precio de venta, IVA, imagen, categoría, activo).',
        module: 'products',
    },
    'products.delete': {
        id: 'products.delete',
        name: 'Eliminar productos',
        description: 'Borrar productos del catálogo (soft-delete).',
        module: 'products',
    },
    'products.viewCost': {
        id: 'products.viewCost',
        name: 'Ver precio de compra',
        description: 'Ver el campo `costo` y la utilidad/margen calculados. Información sensible.',
        module: 'products',
    },
    // -------------------- Categories --------------------
    'categories.view': {
        id: 'categories.view',
        name: 'Ver categorías',
        description: 'Acceder a la lista de categorías.',
        module: 'categories',
    },
    'categories.manage': {
        id: 'categories.manage',
        name: 'Gestionar categorías',
        description: 'Crear, editar y eliminar categorías.',
        module: 'categories',
    },
    // -------------------- Cash --------------------
    'cash.openOwn': {
        id: 'cash.openOwn',
        name: 'Abrir su propia caja',
        description: 'Iniciar sesión de caja para el usuario actual.',
        module: 'cash',
    },
    'cash.closeOwn': {
        id: 'cash.closeOwn',
        name: 'Cerrar su propia caja',
        description: 'Finalizar la sesión de caja del usuario actual con arqueo.',
        module: 'cash',
    },
    'cash.addMovement': {
        id: 'cash.addMovement',
        name: 'Registrar movimientos de caja',
        description: 'Ingresos, egresos y retiros en la propia caja.',
        module: 'cash',
    },
    'cash.viewOthers': {
        id: 'cash.viewOthers',
        name: 'Ver cajas de otros usuarios',
        description: 'Ver el card "Otras cajas abiertas" para auditar a otros cajeros en vivo.',
        module: 'cash',
    },
    'cash.closeOthers': {
        id: 'cash.closeOthers',
        name: 'Cerrar caja ajena',
        description: 'Forzar cierre de la caja de otro usuario (emergencia, ej. fin de turno sin que el cajero cierre).',
        module: 'cash',
    },
    // -------------------- Payments --------------------
    'payments.register': {
        id: 'payments.register',
        name: 'Registrar pagos',
        description: 'Registrar recepción de pago (efectivo, tarjeta, transferencia, Nequi, etc.) contra un pedido.',
        module: 'payments',
    },
    'payments.credit': {
        id: 'payments.credit',
        name: 'Vender a crédito',
        description: 'Cerrar un pedido dejando parte o todo el valor como saldo pendiente a nombre de un cliente.',
        module: 'payments',
        requiresFeature: 'customer_credit',
    },
    'payments.void': {
        id: 'payments.void',
        name: 'Anular pagos',
        description: 'Anular un pago registrado por error mientras el pedido no esté pagado y la caja siga abierta. Si fue en efectivo se registra la devolución en caja.',
        module: 'payments',
    },
    // -------------------- Customers (optional) --------------------
    'customers.view': {
        id: 'customers.view',
        name: 'Ver clientes y cartera',
        description: 'Acceder a la lista de clientes, su saldo pendiente y su historial.',
        module: 'customers',
        requiresFeature: 'customer_credit',
    },
    'customers.manage': {
        id: 'customers.manage',
        name: 'Gestionar clientes',
        description: 'Editar datos de clientes (nombre, teléfono, documento).',
        module: 'customers',
        requiresFeature: 'customer_credit',
    },
    'customers.collect': {
        id: 'customers.collect',
        name: 'Registrar abonos a cartera',
        description: 'Recibir pagos de un cliente contra su saldo de ventas a crédito. Requiere caja abierta del propio usuario.',
        module: 'customers',
        requiresFeature: 'customer_credit',
    },
    // -------------------- Tables (optional) --------------------
    'tables.view': {
        id: 'tables.view',
        name: 'Ver mesas',
        description: 'Acceder al dashboard del salón y seleccionar mesas al crear pedidos.',
        module: 'tables',
        requiresFeature: 'tables',
    },
    'tables.block': {
        id: 'tables.block',
        name: 'Bloquear/desbloquear mesas',
        description: 'Marcar mesas como fuera de servicio desde el plano (sin entrar al editor completo).',
        module: 'tables',
        requiresFeature: 'tables',
    },
    'tables.configure': {
        id: 'tables.configure',
        name: 'Configurar plano de mesas',
        description: 'Editar zonas, agregar/eliminar mesas y mover su posición en el plano.',
        module: 'tables',
        requiresFeature: 'tables',
    },
    // -------------------- Delivery (optional) --------------------
    'delivery.create': {
        id: 'delivery.create',
        name: 'Crear pedidos domicilio',
        description: 'Abrir pedidos con origen "domicilio" y dirección del cliente.',
        module: 'delivery',
        requiresFeature: 'delivery',
    },
    // -------------------- Tips (optional) --------------------
    'tips.edit': {
        id: 'tips.edit',
        name: 'Editar propina',
        description: 'Modificar el campo de propina del pedido.',
        module: 'tips',
        requiresFeature: 'tips',
    },
    // -------------------- Inventory (optional) --------------------
    'inventory.view': {
        id: 'inventory.view',
        name: 'Ver inventario',
        description: 'Ver la lista de stock por producto y los chips de estado en el POS.',
        module: 'inventory',
        requiresFeature: 'inventory',
    },
    'inventory.viewMovements': {
        id: 'inventory.viewMovements',
        name: 'Ver historial de movimientos',
        description: 'Acceder al tab "Movimientos" con el log auditable (entradas, salidas, ajustes, ventas).',
        module: 'inventory',
        requiresFeature: 'inventory',
    },
    'inventory.adjust': {
        id: 'inventory.adjust',
        name: 'Ajustar stock (+/−)',
        description: 'Sumar o restar unidades al stock con motivo (compra, merma, corrección rápida).',
        module: 'inventory',
        requiresFeature: 'inventory',
    },
    'inventory.setQuantity': {
        id: 'inventory.setQuantity',
        name: 'Fijar stock absoluto',
        description: 'Establecer el stock exacto tras conteo físico, junto con umbral de bajo stock.',
        module: 'inventory',
        requiresFeature: 'inventory',
    },
    'inventory.viewCost': {
        id: 'inventory.viewCost',
        name: 'Ver costo en inventario',
        description: 'Ver el precio de compra en la grilla del inventario (info sensible, relacionado con utilidad).',
        module: 'inventory',
        requiresFeature: 'inventory',
    },
    // -------------------- Stats (optional) --------------------
    'stats.view': {
        id: 'stats.view',
        name: 'Ver estadísticas',
        description: 'Acceder al dashboard de ventas — ver # pedidos, ticket promedio y gráficos de volumen por hora / producto / vendedor.',
        module: 'stats',
        requiresFeature: 'stats',
    },
    'stats.viewRevenue': {
        id: 'stats.viewRevenue',
        name: 'Ver ventas totales (plata)',
        description: 'Ver los montos en pesos (totales, por vendedor, por método de pago). Sin este permiso se ven conteos pero no las cifras.',
        module: 'stats',
        requiresFeature: 'stats',
    },
    // -------------------- Expenses (optional) --------------------
    'expenses.view': {
        id: 'expenses.view',
        name: 'Ver gastos',
        description: 'Acceder a la lista de gastos del tenant. Por default solo el propio usuario; con `expenses.viewAll` ve los de todos.',
        module: 'expenses',
        requiresFeature: 'expenses',
    },
    'expenses.viewAll': {
        id: 'expenses.viewAll',
        name: 'Ver gastos de otros usuarios',
        description: 'Sin este permiso, el usuario solo ve los gastos que él registró. Con el permiso, ve los de todos.',
        module: 'expenses',
        requiresFeature: 'expenses',
    },
    'expenses.create': {
        id: 'expenses.create',
        name: 'Registrar gasto',
        description: 'Crear un nuevo gasto. Si paga en efectivo con caja abierta, se descuenta automáticamente de la caja.',
        module: 'expenses',
        requiresFeature: 'expenses',
    },
    'expenses.edit': {
        id: 'expenses.edit',
        name: 'Editar gasto',
        description: 'Modificar datos de un gasto ya registrado (categoría, monto, descripción, fecha). No cambia el movimiento de caja si ya se creó.',
        module: 'expenses',
        requiresFeature: 'expenses',
    },
    'expenses.delete': {
        id: 'expenses.delete',
        name: 'Eliminar gasto',
        description: 'Borrar un gasto. Si tenía movimiento de caja vinculado, también se reversa el movimiento (solo si la sesión sigue abierta).',
        module: 'expenses',
        requiresFeature: 'expenses',
    },
    // -------------------- Settings --------------------
    'settings.view': {
        id: 'settings.view',
        name: 'Ver configuraciones',
        description: 'Acceder a la sección "Configuraciones".',
        module: 'settings',
    },
    'settings.editTenant': {
        id: 'settings.editTenant',
        name: 'Editar datos del tenant',
        description: 'Modificar nombre, NIT, ubicación, etc.',
        module: 'settings',
    },
    'settings.editFeatures': {
        id: 'settings.editFeatures',
        name: 'Activar/desactivar features',
        description: 'Togglear features opcionales (tables, delivery, inventory, kitchen_flow, tips) para el tenant.',
        module: 'settings',
    },
    'settings.editPermissions': {
        id: 'settings.editPermissions',
        name: 'Editar matriz de permisos',
        description: 'Cambiar qué puede hacer cada rol en este tenant. Permiso fundacional — solo el admin lo trae activo.',
        module: 'settings',
    },
    // -------------------- Users --------------------
    'users.view': {
        id: 'users.view',
        name: 'Ver usuarios',
        description: 'Ver la lista de usuarios del tenant.',
        module: 'users',
    },
    'users.manage': {
        id: 'users.manage',
        name: 'Gestionar usuarios',
        description: 'Crear, editar, eliminar usuarios y resetear contraseñas dentro del tenant.',
        module: 'users',
    },
};
export const permissionIds = Object.keys(permissions);
export const isPermissionId = (id) => Object.prototype.hasOwnProperty.call(permissions, id);
// ==================== Default matrix ====================
/**
 * Helper para definir la matriz por extensión — cada rol lista los
 * permisos que tiene activos. Lo que no esté listado = OFF.
 *
 * `superadmin` no aparece acá: es cross-tenant y se maneja afuera de
 * esta matriz (siempre todo permitido en su propio scope).
 */
const rolePermissions = {
    admin: [
        // Acceso total en su tenant. Listamos TODOS para que quede explícito
        // en el código que el admin es el super-operador del tenant.
        'orders.view',
        'orders.viewAll',
        'orders.create',
        'orders.editDraft',
        'orders.advance',
        'orders.charge',
        'orders.cancel',
        'orders.applyDiscount',
        'products.view',
        'products.create',
        'products.edit',
        'products.delete',
        'products.viewCost',
        'categories.view',
        'categories.manage',
        'cash.openOwn',
        'cash.closeOwn',
        'cash.addMovement',
        'cash.viewOthers',
        'cash.closeOthers',
        'payments.register',
        'payments.credit',
        'payments.void',
        'customers.view',
        'customers.manage',
        'customers.collect',
        'tables.view',
        'tables.block',
        'tables.configure',
        'delivery.create',
        'tips.edit',
        'inventory.view',
        'inventory.viewMovements',
        'inventory.adjust',
        'inventory.setQuantity',
        'inventory.viewCost',
        'stats.view',
        'stats.viewRevenue',
        'expenses.view',
        'expenses.viewAll',
        'expenses.create',
        'expenses.edit',
        'expenses.delete',
        'settings.view',
        'settings.editTenant',
        'settings.editFeatures',
        'settings.editPermissions',
        'users.view',
        'users.manage',
    ],
    manager: [
        // Opera + audita + ajusta catálogo. NO gestiona usuarios ni edita
        // permisos (política) y NO cierra cajas ajenas a la fuerza.
        'orders.view',
        'orders.viewAll',
        'orders.create',
        'orders.editDraft',
        'orders.advance',
        'orders.charge',
        'orders.cancel',
        'orders.applyDiscount',
        'products.view',
        'products.create',
        'products.edit',
        // NO products.delete — borrar catálogo es del admin.
        'products.viewCost',
        'categories.view',
        'categories.manage',
        'cash.openOwn',
        'cash.closeOwn',
        'cash.addMovement',
        'cash.viewOthers',
        'payments.register',
        'payments.credit',
        'payments.void',
        'customers.view',
        'customers.manage',
        'customers.collect',
        'tables.view',
        'tables.block',
        // NO tables.configure — editor del plano es del admin.
        'delivery.create',
        'tips.edit',
        'inventory.view',
        'inventory.viewMovements',
        'inventory.adjust',
        'inventory.setQuantity',
        'inventory.viewCost',
        'stats.view',
        'stats.viewRevenue',
        'expenses.view',
        'expenses.viewAll',
        'expenses.create',
        'expenses.edit',
        // NO expenses.delete — borrar registro contable es del admin.
        'settings.view',
        'users.view',
    ],
    cashier: [
        // Vende, cobra, abre/cierra SU caja. Catálogo e inventario solo
        // lectura. No ve cajas de otros, no cancela pedidos por default.
        'orders.view',
        'orders.create',
        'orders.editDraft',
        'orders.advance',
        'orders.charge',
        'orders.cancel',
        'products.view',
        'categories.view',
        'cash.openOwn',
        'cash.closeOwn',
        'cash.addMovement',
        'payments.register',
        // NO payments.credit (fiar) ni payments.void — política del negocio,
        // el admin puede habilitarlos por tenant desde la matriz.
        'customers.view',
        'customers.collect',
        'tables.view',
        'delivery.create',
        'tips.edit',
        'inventory.view',
    ],
    waiter: [
        // Toma pedidos y los mueve hasta entregado. NO cobra, NO cancela,
        // NO ve pedidos de otros meseros por default.
        'orders.view',
        'orders.create',
        'orders.editDraft',
        'orders.advance',
        'products.view',
        'categories.view',
        'tables.view',
    ],
};
const buildDefaultMatrix = () => {
    const toSet = (ids) => new Set(ids.filter(isPermissionId));
    return {
        superadmin: new Set(permissionIds), // todo permitido en su tenant scope
        admin: toSet(rolePermissions.admin),
        manager: toSet(rolePermissions.manager),
        cashier: toSet(rolePermissions.cashier),
        waiter: toSet(rolePermissions.waiter),
    };
};
export const DEFAULT_PERMISSION_MATRIX = buildDefaultMatrix();
/**
 * Evalúa si un rol puede ejecutar un permiso para un tenant dado, con
 * sus overrides aplicados sobre el default. Permiso desconocido → false
 * (fail-closed; defensa contra typos/deploys desfasados).
 */
export const hasPermission = (role, permissionId, overrides = []) => {
    if (!isPermissionId(permissionId))
        return false;
    const override = overrides.find((o) => o.role === role && o.permissionId === permissionId);
    if (override)
        return override.granted;
    return DEFAULT_PERMISSION_MATRIX[role].has(permissionId);
};
//# sourceMappingURL=index.js.map